import type { ComponentType } from "react";
import { useWorkspaceCloses } from "@api/close.api";
import { useSortedCloseStages } from "@api/organizationCloseStage.api";
import type { TaskUpdateBody } from "@api/task.api";
import { useWorkspaceTaskGroups } from "@api/taskGroup.api";
import { fullDateFullYearFormat } from "@common/helpers/date";
import { TaskColumn } from "@common/types/views/tasksView";
import {
    CellText,
    createColumns,
    type DataGridCellProps,
    type DataGridColumn,
    type DataGridColumnOptions,
    type DataGridListCommit,
} from "@design-system/DataGrid";
import { formatDuration } from "@design-system/TimePicker/TimePicker.lib";
import { formatDateOnly } from "@helpers/date";
import { TaskPriority } from "../TaskPrioritySelect/TaskPriority";
import { NO_PRIORITY_LABEL } from "../TaskPrioritySelect/TaskPrioritySelect";
import { TASK_PRIORITY_CONFIG } from "../TaskPrioritySelect/taskPriority.constant";
import { TaskStatus } from "../TaskStatusSelect/TaskStatus";
import type { NestedTaskRow } from "../types";
import { TaskCloseName } from "./cells/TaskCloseName";
import {
    TaskDueDateSummary,
    TaskEstimateSummary,
} from "./cells/taskGroupSummaries";
import {
    hasTaskFields,
    type TaskListCell,
    type TaskListTaskRow,
} from "./cells/taskRowCells";
import type { TaskListActions } from "./taskListActions";
import {
    PRIORITY_OPTIONS,
    STATUS_OPTIONS,
    useGroupOptions,
    useServiceOptions,
    useStageOptions,
} from "./taskPickerOptions";
import { getServiceUpdate } from "../taskClose";

export const col = createColumns<NestedTaskRow>();

/** Sorting is on the server and the list has no search or copy, so text columns read no value. */
export const NO_VALUE = () => undefined;

/** What each editable column's builder takes: the config's layout and the list's actions. */
export interface TaskColumnBuild {
    options: Omit<DataGridColumnOptions<NestedTaskRow>, "render" | "edit">;
    actions: TaskListActions;
}

/** A cell showing `TaskValue` on task and draft rows, nothing on group rows. */
export const toTaskCell = (
    TaskValue: ComponentType<{ task: TaskListTaskRow }>,
): TaskListCell => {
    const TaskCell = ({ context }: DataGridCellProps<NestedTaskRow>) => {
        const task = context.row.original;
        return hasTaskFields(task) ? <TaskValue task={task} /> : null;
    };
    return TaskCell;
};

/** An editor's commit as a task update; group rows never edit. */
export const commitTask =
    <TValue,>({
        actions,
        toBody,
    }: {
        actions: TaskListActions;
        toBody: (value: TValue) => TaskUpdateBody;
    }): DataGridListCommit<NestedTaskRow, TValue> =>
    ({ row, value }) =>
        hasTaskFields(row)
            ? actions.update({ task: row, body: toBody(value) })
            : undefined;

const textSource = (id: TaskColumn) => ({ id, value: NO_VALUE });

const StatusValue = ({ task }: { task: TaskListTaskRow }) => (
    <TaskStatus status={task.status} showText />
);

const PriorityValue = ({ task }: { task: TaskListTaskRow }) => {
    if (!task.priority) {
        return null;
    }
    const { label } = TASK_PRIORITY_CONFIG[task.priority];
    return (
        <span
            role="img"
            aria-label={label}
            data-cell-tooltip={label}
            data-cell-tooltip-always=""
        >
            <TaskPriority priority={task.priority} variant="iconOnly" />
        </span>
    );
};

const GroupName = ({ id }: { id: string }) => {
    const { data: groups = [] } = useWorkspaceTaskGroups();
    const name = groups.find((group) => group.id === id)?.name;
    return name ? <CellText text={name} overflow="truncate" /> : null;
};

const GroupValue = ({ task }: { task: TaskListTaskRow }) =>
    task.groupId ? <GroupName id={task.groupId} /> : null;

const ServiceName = ({ id }: { id: string }) => {
    const { data: closes = [] } = useWorkspaceCloses();
    return <TaskCloseName id={id} items={closes} />;
};

const ServiceValue = ({ task }: { task: TaskListTaskRow }) =>
    task.closeId ? <ServiceName id={task.closeId} /> : null;

const StageName = ({ id }: { id: string }) => {
    const stages = useSortedCloseStages();
    return <TaskCloseName id={id} items={stages} />;
};

const StageValue = ({ task }: { task: TaskListTaskRow }) =>
    task.closeStageId ? <StageName id={task.closeStageId} /> : null;

const DueDateValue = ({ task }: { task: TaskListTaskRow }) =>
    task.dueDate ? (
        <CellText
            text={formatDateOnly(task.dueDate, fullDateFullYearFormat)}
            overflow="truncate"
        />
    ) : null;

const EstimateValue = ({ task }: { task: TaskListTaskRow }) =>
    task.estimatedTime != null ? (
        <CellText
            text={formatDuration(task.estimatedTime)}
            overflow="truncate"
        />
    ) : null;

/** Status: every status the UI offers; finishing a blocked task asks first. */
export const buildStatusColumn = ({ options, actions }: TaskColumnBuild) =>
    col.text(textSource(TaskColumn.STATUS), {
        ...options,
        render: toTaskCell(StatusValue),
        edit: col.edit.picker({
            options: () => STATUS_OPTIONS,
            value: (row) => row.status ?? null,
            commit: ({ row, value }) =>
                hasTaskFields(row) && value
                    ? actions.changeStatus({ task: row, status: value })
                    : undefined,
            placeholder: "Set status",
        }),
    });

/** Priority: its icon, named in a tooltip. */
export const buildPriorityColumn = ({ options, actions }: TaskColumnBuild) =>
    col.text(textSource(TaskColumn.PRIORITY), {
        ...options,
        render: toTaskCell(PriorityValue),
        edit: col.edit.picker({
            options: () => PRIORITY_OPTIONS,
            emptyOption: {
                label: NO_PRIORITY_LABEL,
                leading: <TaskPriority priority={null} variant="iconOnly" />,
            },
            value: (row) => row.priority ?? null,
            commit: commitTask({
                actions,
                toBody: (priority) => ({ priority }),
            }),
            placeholder: "Set priority",
            layout: "fit",
        }),
    });

/** Group: the row's workspace task groups, loaded once the picker opens. */
export const buildGroupColumn = ({ options, actions }: TaskColumnBuild) =>
    col.text(textSource(TaskColumn.GROUP), {
        ...options,
        render: toTaskCell(GroupValue),
        edit: col.edit.picker({
            useOptions: useGroupOptions,
            emptyOption: { label: "No group" },
            value: (row) => row.groupId ?? null,
            commit: commitTask({ actions, toBody: (groupId) => ({ groupId }) }),
            placeholder: "Add group",
        }),
    });

/** Service: the row's workspace closes; clearing it clears the stage too. */
export const buildServiceColumn = ({ options, actions }: TaskColumnBuild) =>
    col.text(textSource(TaskColumn.CLOSE), {
        ...options,
        render: toTaskCell(ServiceValue),
        edit: col.edit.picker({
            useOptions: useServiceOptions,
            emptyOption: { label: "No service" },
            value: (row) => row.closeId ?? null,
            commit: commitTask({ actions, toBody: getServiceUpdate }),
            placeholder: "Add service",
        }),
    });

/** Stage: the organization's close stages. */
export const buildStageColumn = ({ options, actions }: TaskColumnBuild) =>
    col.text(textSource(TaskColumn.CLOSE_STAGE), {
        ...options,
        render: toTaskCell(StageValue),
        edit: col.edit.picker({
            useOptions: useStageOptions,
            emptyOption: { label: "No stage" },
            value: (row) => row.closeStageId ?? null,
            commit: commitTask({
                actions,
                toBody: (closeStageId) => ({ closeStageId }),
            }),
            placeholder: "Add stage",
        }),
    });

/** Due date: a clearable day; collapsed groups show their latest open one. */
export const buildDueDateColumn = ({
    options,
    actions,
}: TaskColumnBuild): DataGridColumn<NestedTaskRow> =>
    col.date(
        { id: TaskColumn.DUE_DATE, value: (row) => row.dueDate },
        {
            ...options,
            render: toTaskCell(DueDateValue),
            summary: TaskDueDateSummary,
            edit: col.edit.date({
                value: (row) => row.dueDate ?? null,
                commit: commitTask({
                    actions,
                    toBody: (dueDate) => ({ dueDate }),
                }),
                placeholder: "Add due date",
            }),
        },
    );

/** Estimate: minutes; collapsed groups show their total. */
export const buildEstimateColumn = ({ options, actions }: TaskColumnBuild) =>
    col.text(textSource(TaskColumn.ESTIMATE), {
        ...options,
        render: toTaskCell(EstimateValue),
        summary: TaskEstimateSummary,
        edit: col.edit.duration({
            value: (row) => row.estimatedTime ?? null,
            commit: commitTask({
                actions,
                toBody: (estimatedTime) => ({ estimatedTime }),
            }),
            placeholder: "Add estimate",
            label: "Estimate",
            removeLabel: "Remove estimate",
        }),
    });
